import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import Layout from '../components/Layout';

interface DocsData {
  title: string;
  version: string;
  baseUrl: string;
  description: string;
  sections: any[];
  jobTypes: any[];
  permissions: any[];
  priorities: any[];
  statusCodes: any[];
  webSocketEvents?: any[];
  errorCodes?: any[];
  examples: any;
  websocketLoginFlow?: any;
}

const WhatsAppDocs: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [docsData, setDocsData] = useState<DocsData | null>(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    fetchDocsData();
  }, []);

  // Update active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!docsData) return;
      const sections = ['overview', 'authentication', ...docsData.sections.filter(s => s.id !== 'overview' && s.id !== 'authentication').map(s => s.id), 'websocket-flow', 'reference', 'examples'];
      
      let current = '';
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100) {
            current = section;
          }
        }
      }
      if (current && current !== activeSection) {
        setActiveSection(current);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [docsData, activeSection]);

  const fetchDocsData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/docs/whatsapp');
      const result = await response.json();
      if (result.success) {
        setDocsData(result.data);
      } else {
        setError('Không thể tải tài liệu API');
      }
    } catch (error) {
      console.error('Failed to fetch docs:', error);
      setError('Không thể tải tài liệu API');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => {
        setCopiedId(null);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      const top = element.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const getMenuItems = () => {
    if (!docsData) return [];
    
    const items = [
      { id: 'overview', label: 'Overview', icon: '📖' },
      { id: 'authentication', label: 'Authentication', icon: '🔑' }
    ];

    docsData.sections.forEach(section => {
      if (section.id !== 'overview' && section.id !== 'authentication') {
        items.push({
          id: section.id,
          label: section.title.replace(/🔵|🔴|🟡|📨|📋|🔗|🔄/g, '').trim(),
          icon: section.title.includes('🔵') ? '🔵' : 
                section.title.includes('🔴') ? '🔴' :
                section.title.includes('🟡') ? '🟡' :
                section.title.includes('📨') ? '📨' :
                section.title.includes('📋') ? '📋' :
                section.title.includes('🔗') ? '🔗' :
                section.title.includes('🔄') ? '🔄' : '📄'
        });
      }
    });

    if (docsData.websocketLoginFlow) {
      items.push({ id: 'websocket-flow', label: 'WebSocket Flow', icon: '🔌' });
    }
    
    items.push(
      { id: 'reference', label: 'Reference', icon: '📚' },
      { id: 'examples', label: 'Examples', icon: '💻' }
    );

    return items;
  };

  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET': return { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' }; // Green
      case 'POST': return { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' }; // Blue
      case 'PUT': return { bg: '#fef3c7', text: '#92400e', border: '#fde68a' }; // Yellow
      case 'DELETE': return { bg: '#fee2e2', text: '#b91c1c', border: '#fecaca' }; // Red
      default: return { bg: '#f1f5f9', text: '#475569', border: '#e2e8f0' }; // Gray
    }
  };

  // --- Styled Components Replacements (Inline Objects) ---

  const styles = {
    layout: { display: 'flex', minHeight: '100vh', backgroundColor: '#ffffff', fontFamily: 'Inter, system-ui, sans-serif' },
    sidebar: {
      width: '280px',
      flexShrink: 0,
      backgroundColor: '#f8fafc',
      borderRight: '1px solid #e2e8f0',
      position: 'fixed' as 'fixed',
      top: '60px', // Assuming a top navbar exists, adjust as needed
      bottom: 0,
      overflowY: 'auto' as 'auto',
      padding: '24px 16px',
      zIndex: 10
    },
    mainContent: {
      flex: 1,
      marginLeft: '280px',
      padding: '40px 48px',
      maxWidth: '1400px'
    },
    menuItem: (isActive: boolean) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '10px 14px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: isActive ? '600' : '500',
      color: isActive ? '#0f172a' : '#64748b',
      backgroundColor: isActive ? '#e2e8f0' : 'transparent',
      transition: 'all 0.2s ease',
      border: 'none',
      width: '100%',
      textAlign: 'left' as 'left',
      marginBottom: '4px'
    }),
    endpointContainer: {
      display: 'flex',
      gap: '40px',
      marginBottom: '60px',
      paddingTop: '20px',
      borderTop: '1px solid #e2e8f0',
      flexDirection: 'row' as 'row',
      alignItems: 'flex-start' as 'flex-start'
    },
    endpointInfo: {
      flex: 1,
      minWidth: 0 // Prevent flex blowout
    },
    endpointCode: {
      flex: 1,
      minWidth: 0,
      position: 'sticky' as 'sticky',
      top: '80px'
    },
    codeBlock: {
      backgroundColor: '#0f172a',
      color: '#f8fafc',
      borderRadius: '12px',
      overflow: 'hidden',
      marginBottom: '16px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    codeHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 16px',
      backgroundColor: '#1e293b',
      borderBottom: '1px solid #334155'
    },
    pre: {
      margin: 0,
      padding: '16px',
      fontSize: '13px',
      fontFamily: '"Fira Code", monospace',
      overflowX: 'auto' as 'auto',
      lineHeight: 1.5
    },
    badge: {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '9999px',
      fontSize: '12px',
      fontWeight: '600',
      marginRight: '8px'
    },
    h2: { fontSize: '28px', fontWeight: '700', color: '#0f172a', marginBottom: '24px', paddingBottom: '8px', borderBottom: '2px solid #e2e8f0' },
    h3: { fontSize: '20px', fontWeight: '600', color: '#0f172a', marginBottom: '16px', marginTop: '32px' },
    p: { fontSize: '15px', color: '#475569', lineHeight: 1.6, marginBottom: '16px' }
  };

  const CodeSnippet = ({ title, code, id }: { title: string, code: string, id: string }) => (
    <div style={styles.codeBlock}>
      <div style={styles.codeHeader}>
        <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase' }}>{title}</span>
        <button
          onClick={() => copyToClipboard(code, id)}
          style={{
            background: 'transparent',
            border: 'none',
            color: copiedId === id ? '#10b981' : '#94a3b8',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          {copiedId === id ? '✓ Copied' : 'Copy'}
        </button>
      </div>
      <pre style={styles.pre}>
        <code>{code}</code>
      </pre>
    </div>
  );

  const MarkdownComponents = {
    p: ({children}: any) => <p style={styles.p}>{children}</p>,
    strong: ({children}: any) => <strong style={{fontWeight: '600', color: '#0f172a'}}>{children}</strong>,
    code: ({children}: any) => <code style={{backgroundColor: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontSize: '13px', fontFamily: '"Fira Code", monospace', color: '#db2777'}}>{children}</code>,
    ul: ({children}: any) => <ul style={{...styles.p, paddingLeft: '24px', listStyleType: 'disc'}}>{children}</ul>,
    li: ({children}: any) => <li style={{marginBottom: '8px'}}>{children}</li>
  };


  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <div style={{ width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTop: '4px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        </div>
      </Layout>
    );
  }

  if (error || !docsData) {
    return (
      <Layout>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <h2 style={{ color: '#ef4444' }}>{error || 'No data found'}</h2>
          <button onClick={fetchDocsData} style={{ marginTop: '16px', padding: '8px 16px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Retry</button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.layout}>
        {/* Sidebar Navigation */}
        <aside style={styles.sidebar}>
          <div style={{ marginBottom: '24px', paddingLeft: '14px' }}>
            <h1 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 }}>API Reference</h1>
            <span style={{ fontSize: '12px', color: '#64748b' }}>v{docsData.version}</span>
          </div>
          <nav>
            {getMenuItems().map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                style={styles.menuItem(activeSection === item.id)}
              >
                <span>{item.icon}</span>
                {item.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main style={styles.mainContent}>
          {/* Header */}
          <div style={{ marginBottom: '48px' }}>
            <h1 style={{ fontSize: '40px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.02em', marginBottom: '16px' }}>{docsData.title}</h1>
            <p style={{ fontSize: '18px', color: '#475569', maxWidth: '800px', lineHeight: 1.6 }}>{docsData.description}</p>
            <div style={{ marginTop: '24px', display: 'inline-flex', alignItems: 'center', backgroundColor: '#f1f5f9', padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <span style={{ color: '#64748b', marginRight: '8px', fontSize: '14px', fontWeight: '500' }}>Base URL</span>
              <code style={{ color: '#0f172a', fontFamily: '"Fira Code", monospace', fontSize: '14px', fontWeight: '600' }}>{docsData.baseUrl}</code>
            </div>
          </div>

          {/* Overview & Auth (Single Column) */}
          {['overview', 'authentication'].map(sectionId => {
            const section = docsData.sections.find(s => s.id === sectionId);
            if (!section) return null;
            return (
              <div key={section.id} id={section.id} style={{ marginBottom: '60px', maxWidth: '800px' }}>
                <h2 style={styles.h2}>{section.title}</h2>
                <ReactMarkdown components={MarkdownComponents}>{section.content}</ReactMarkdown>
                
                {/* Specific rendering for Auth Types */}
                {section.authTypes && (
                  <div style={{ display: 'grid', gap: '16px', marginTop: '24px' }}>
                    {section.authTypes.map((auth: any, idx: number) => (
                      <div key={idx} style={{ padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', marginBottom: '8px' }}>{auth.type}</h4>
                        <p style={{ fontSize: '14px', color: '#475569', marginBottom: '12px' }}>{auth.description}</p>
                        <div style={{ backgroundColor: '#0f172a', padding: '12px', borderRadius: '8px', color: '#f8fafc', fontFamily: 'monospace', fontSize: '13px' }}>
                          {auth.headers.map((h: string, i: number) => <div key={i}>{h}</div>)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* API Endpoints (Two Column Split) */}
          {docsData.sections.filter(s => s.id !== 'overview' && s.id !== 'authentication').map(section => (
            <div key={section.id} id={section.id} style={{ marginBottom: '80px' }}>
              <h2 style={{...styles.h2, maxWidth: '800px'}}>{section.title}</h2>
              <div style={{ maxWidth: '800px', marginBottom: '40px' }}>
                <ReactMarkdown components={MarkdownComponents}>{section.content}</ReactMarkdown>
              </div>

              {section.endpoints?.map((endpoint: any, idx: number) => {
                const methodColors = getMethodColor(endpoint.method);
                return (
                  <div key={idx} style={styles.endpointContainer}>
                    {/* Left Column: Info */}
                    <div style={styles.endpointInfo}>
                      <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '12px', marginTop: 0 }}>{endpoint.title}</h3>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
                        <span style={{
                          ...styles.badge,
                          backgroundColor: methodColors.bg,
                          color: methodColors.text,
                          border: `1px solid ${methodColors.border}`,
                          fontSize: '13px',
                          padding: '4px 12px'
                        }}>
                          {endpoint.method}
                        </span>
                        <code style={{ fontSize: '15px', color: '#0f172a', fontWeight: '600', fontFamily: '"Fira Code", monospace' }}>{endpoint.path}</code>
                      </div>
                      
                      <p style={styles.p}>{endpoint.description}</p>

                      {endpoint.permissions && (
                        <div style={{ marginBottom: '24px' }}>
                          <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '8px', textTransform: 'uppercase' }}>Requires Permissions</span>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {endpoint.permissions.map((p: string, i: number) => (
                              <span key={i} style={{ backgroundColor: '#f1f5f9', color: '#475569', padding: '4px 10px', borderRadius: '6px', fontSize: '13px', border: '1px solid #e2e8f0', fontFamily: 'monospace' }}>{p}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {endpoint.supportedJobTypes && (
                        <div style={{ marginBottom: '24px' }}>
                          <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '8px', textTransform: 'uppercase' }}>Supported Job Types</span>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {endpoint.supportedJobTypes.map((j: string, i: number) => (
                              <span key={i} style={{ backgroundColor: '#f0fdf4', color: '#166534', padding: '4px 10px', borderRadius: '6px', fontSize: '13px', border: '1px solid #bbf7d0', fontFamily: 'monospace' }}>{j}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {endpoint.notes && endpoint.notes.length > 0 && (
                        <div style={{ backgroundColor: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
                          <span style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a', display: 'block', marginBottom: '8px' }}>📝 Notes</span>
                          <ul style={{ margin: 0, paddingLeft: '20px', color: '#475569', fontSize: '14px', lineHeight: 1.6 }}>
                            {endpoint.notes.map((n: string, i: number) => <li key={i}>{n}</li>)}
                          </ul>
                        </div>
                      )}
                      
                      {/* Sub-examples within info (for bulk update or specific cases) */}
                      {endpoint.examples && endpoint.examples.length > 0 && (
                         <div style={{ marginTop: '32px' }}>
                            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '16px' }}>Specific Cases</h4>
                            {endpoint.examples.map((ex: any, i: number) => (
                              <div key={i} style={{ marginBottom: '24px', backgroundColor: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <strong style={{ color: '#0f172a', display: 'block', marginBottom: '4px' }}>{ex.title}</strong>
                                {ex.description && <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 12px 0' }}>{ex.description}</p>}
                                {ex.curl && (
                                  <div style={{ backgroundColor: '#0f172a', padding: '12px', borderRadius: '6px', overflowX: 'auto', marginBottom: ex.body || ex.websocketEvent ? '12px' : 0 }}>
                                    <pre style={{ margin: 0, color: '#f8fafc', fontSize: '12px', fontFamily: 'monospace' }}>{ex.curl}</pre>
                                  </div>
                                )}
                              </div>
                            ))}
                         </div>
                      )}
                    </div>

                    {/* Right Column: Code Snippets */}
                    <div style={styles.endpointCode}>
                      {endpoint.curl && (
                        <CodeSnippet 
                          title="cURL Request" 
                          code={endpoint.curl} 
                          id={`curl-${section.id}-${idx}`} 
                        />
                      )}
                      
                      {endpoint.body && (
                        <CodeSnippet 
                          title="Body Payload (JSON)" 
                          code={JSON.stringify(endpoint.body, null, 2)} 
                          id={`body-${section.id}-${idx}`} 
                        />
                      )}

                      {endpoint.response && (
                        <CodeSnippet 
                          title="Response" 
                          code={JSON.stringify(endpoint.response, null, 2)} 
                          id={`res-${section.id}-${idx}`} 
                        />
                      )}

                      {endpoint.websocketEvent && (
                        <CodeSnippet 
                          title="WebSocket Event" 
                          code={JSON.stringify(endpoint.websocketEvent, null, 2)} 
                          id={`ws-${section.id}-${idx}`} 
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {/* WebSocket Login Flow (Two Column if steps exist) */}
          {docsData.websocketLoginFlow && (
            <div id="websocket-flow" style={{ marginBottom: '80px', maxWidth: '800px' }}>
              <h2 style={styles.h2}>{docsData.websocketLoginFlow.title}</h2>
              <p style={styles.p}>{docsData.websocketLoginFlow.description}</p>
              
              {docsData.websocketLoginFlow.steps && docsData.websocketLoginFlow.steps.map((step: any, idx: number) => (
                <div key={idx} style={{...styles.endpointContainer, flexDirection: 'column'}}>
                   <div style={{...styles.endpointInfo, borderLeft: '3px solid #3b82f6', paddingLeft: '16px'}}>
                     <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: '0 0 8px 0' }}>Step {step.step}: {step.title}</h3>
                     <p style={{...styles.p, margin: '0 0 12px 0'}}>{step.description}</p>
                     {step.clientAction && (
                       <div style={{ marginBottom: '12px' }}>
                         <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '4px' }}>Client Action</span>
                         <span style={{ color: '#0f172a', fontSize: '14px' }}>{step.clientAction}</span>
                       </div>
                     )}
                   </div>
                   
                   <div style={{...styles.endpointCode, width: '100%', position: 'static'}}>
                      {step.serverResponse && typeof step.serverResponse === 'object' && (
                        <CodeSnippet 
                          title="Server Response (WebSocket)" 
                          code={JSON.stringify(step.serverResponse, null, 2)} 
                          id={`ws-step-${idx}`} 
                        />
                      )}
                      {step.serverResponse && typeof step.serverResponse === 'string' && (
                        <div style={{ backgroundColor: '#f1f5f9', padding: '12px', borderRadius: '8px', color: '#475569', fontSize: '14px', marginBottom: '16px' }}>
                          <strong>Server Response:</strong> {step.serverResponse}
                        </div>
                      )}
                      {step.code && (
                        <CodeSnippet 
                          title="Client Code Example" 
                          code={step.code} 
                          id={`ws-code-${idx}`} 
                        />
                      )}
                   </div>
                </div>
              ))}
            </div>
          )}

          {/* Code Examples Section */}
          {docsData.examples && (
            <div id="examples" style={{ marginBottom: '80px', maxWidth: '1000px' }}>
              <h2 style={styles.h2}>💻 Complete Code Examples</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {docsData.examples.nodejs && (
                  <CodeSnippet 
                    title="Node.js Client Example" 
                    code={docsData.examples.nodejs} 
                    id="example-nodejs" 
                  />
                )}
                {docsData.examples.curl && (
                  <CodeSnippet 
                    title="cURL Batch Examples" 
                    code={docsData.examples.curl} 
                    id="example-curl" 
                  />
                )}
              </div>
            </div>
          )}

          {/* Reference Section (Single Column) */}
          <div id="reference" style={{ marginBottom: '80px', maxWidth: '800px' }}>
            <h2 style={styles.h2}>📚 Reference Data</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div>
                <h3 style={styles.h3}>Job Types</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {docsData.jobTypes.map((item: any, i: number) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <code style={{ color: '#db2777', fontWeight: '600', fontSize: '13px' }}>{item.value}</code>
                      <span style={{ color: '#64748b', fontSize: '13px' }}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 style={styles.h3}>Status Codes</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {docsData.statusCodes.map((item: any, i: number) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <code style={{ color: '#0ea5e9', fontWeight: '600', fontSize: '13px' }}>{item.value}</code>
                      <span style={{ color: '#64748b', fontSize: '13px' }}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 style={styles.h3}>Priorities</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {docsData.priorities.map((item: any, i: number) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <code style={{ color: '#f59e0b', fontWeight: '600', fontSize: '13px' }}>{item.value}</code>
                      <span style={{ color: '#64748b', fontSize: '13px' }}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 style={styles.h3}>Permissions</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {docsData.permissions.map((item: any, i: number) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                      <code style={{ color: '#10b981', fontWeight: '600', fontSize: '13px' }}>{item.value}</code>
                      <span style={{ color: '#64748b', fontSize: '13px' }}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </main>
      </div>
    </Layout>
  );
};

export default WhatsAppDocs;
